import { Dispatch, SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "@/redux/hooks";
import { clearGlobalSearch } from "@/pages/Private/redux/search/search.slice";
import { useAddPersonToSpecificListBulkMutation } from "@/pages/Private/redux/list/list.api";

import { SelectedLinkedInItem } from "./singleSelect";

interface UseBulkAddToListArgs {
	selectedContactIds: number[];
	setSelectedContactIds: Dispatch<SetStateAction<number[]>>;
	selectedLinkedInItems: SelectedLinkedInItem[];
	setSelectedLinkedInItems: Dispatch<SetStateAction<SelectedLinkedInItem[]>>;
	isEnrichAllEmails: boolean;
	isEnrichAllPhones: boolean;
	targetList?: { id: number; name: string };
	setIsOpened: Dispatch<SetStateAction<boolean>>;
	// The modal's snackbar opener — useSnackBar is per-instance and only the modal's is rendered.
	handleOpenSnackbar: (text: string, autoHideDuration?: number) => void;
	// Fired after a successful add — the modal bumps its addedSignal so the By URL tab resets.
	onAddSuccess: () => void;
	// Fired after every attempt (success or failure) — the modal closes the list popover.
	onAddSettled: () => void;
	// Fired after a successful add only — lets a host whose data hangs off the list refetch it.
	onAddToListSuccess?: (addedCount: number) => void;
}

interface UseBulkAddToListResult {
	isBulkAdding: boolean;
	handleAddToList: (list: { id?: number; name: string }) => Promise<void>;
}

/**
 * Encapsulates the multi-select "Add to list" flow of GlobalSearchModal: mapping the two
 * selection buckets to bulk-add payload entries (prospectId / companyEnrichId / linkedinUrl) and
 * running the bulk mutation. Kept out of the modal to keep it under the file-size limit.
 */
export function useBulkAddToList({
	selectedContactIds,
	setSelectedContactIds,
	selectedLinkedInItems,
	setSelectedLinkedInItems,
	isEnrichAllEmails,
	isEnrichAllPhones,
	targetList,
	setIsOpened,
	handleOpenSnackbar,
	onAddSuccess,
	onAddSettled,
	onAddToListSuccess,
}: UseBulkAddToListArgs): UseBulkAddToListResult {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const [bulkAddPersons, { isLoading: isBulkAdding }] = useAddPersonToSpecificListBulkMutation();

	const handleAddToList = useCallback(
		async (list: { id?: number; name: string }) => {
			try {
				const persons = [
					...selectedContactIds.map((id) => ({
						prospectId: id,
						enrichEmail: isEnrichAllEmails,
						enrichPhone: isEnrichAllPhones,
					})),
					...selectedLinkedInItems.map((li) => {
						// Resolved-by-URL prospects already exist in the company: add by prospectId so
						// the backend returns them synchronously in `enriched` (no re-enrichment).
						if (li.prospectId !== undefined) {
							return {
								prospectId: li.prospectId,
								enrichEmail: isEnrichAllEmails,
								enrichPhone: isEnrichAllPhones,
							};
						}

						if (li.companyEnrichId !== undefined) {
							return {
								companyEnrichId: String(li.companyEnrichId),
								enrichEmail: isEnrichAllEmails,
								enrichPhone: isEnrichAllPhones,
							};
						}

						return {
							linkedinUrl: li.linkedinUrl,
							enrichEmail: isEnrichAllEmails,
							enrichPhone: isEnrichAllPhones,
						};
					}),
				];

				await bulkAddPersons({
					listId: list.id ?? null,
					listName: list.id ? null : list.name,
					persons,
				}).unwrap();

				handleOpenSnackbar(
					t("contacts.addedToList", { count: persons.length, name: list.name }),
					6000
				);
				// Signal the By URL tab to reset its pasted chips after a successful add.
				onAddSuccess();
				// The host's own list-hung data (the Feed's cards) is stale now.
				onAddToListSuccess?.(persons.length);

				// List-scoped mode is a focused "add to this list" action: close on success.
				if (targetList) {
					dispatch(clearGlobalSearch());
					setIsOpened(false);
				}
			} catch {
				handleOpenSnackbar(t("basics.error"));
			}

			onAddSettled();
			setSelectedContactIds([]);
			setSelectedLinkedInItems([]);
		},
		[
			selectedContactIds,
			selectedLinkedInItems,
			isEnrichAllEmails,
			isEnrichAllPhones,
			bulkAddPersons,
			handleOpenSnackbar,
			t,
			targetList,
			dispatch,
			setIsOpened,
			onAddSuccess,
			onAddSettled,
			onAddToListSuccess,
			setSelectedContactIds,
			setSelectedLinkedInItems,
		]
	);

	return { isBulkAdding, handleAddToList };
}
